import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import api, { redirectToLogin } from '../services/api';
import { UserDetailsDto } from '../types/UserDetailsDto';

const ADMIN_TOKEN_BACKUP_KEY = 'adminTokenBackup';

/** Set before admin read-only impersonation; MainLayout opens this document after navigation. */
export const ADMIN_PENDING_DOCUMENT_NAME_KEY = 'narralizer.adminPendingDocumentName';

export type StartImpersonationOptions = {
  documentName?: string;
};

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    isLoggingOut: boolean;
    user: UserDetailsDto | null;
    isImpersonating: boolean;
    impersonatorUsername: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (username: string, email: string, password: string) => Promise<void>;
    getToken: () => string | null;
    fetchUserDetails: () => Promise<void>;
    setToken: (token: string) => void;
    updateUser: (user: UserDetailsDto) => void;
    startImpersonation: (userId: number, options?: StartImpersonationOptions) => Promise<void>;
    endImpersonation: () => Promise<void>;
    expiredAccount: { message: string; renewalUrl: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface ErrorResponse {
    status: number;
    message: string;
    renewalUrl?: string;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [user, setUser] = useState<UserDetailsDto | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [expiredAccount, setExpiredAccount] = useState<{ message: string; renewalUrl: string } | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const publicRoutes = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/verify'
    ];

    const clearAuth = () => {
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY);
    };

    const isImpersonating = Boolean(user?.impersonating);
    const impersonatorUsername = user?.impersonatorUsername ?? null;

    const fetchUserDetails = async () => {
        try {
            const response = await api.get('/auth/user');
            setUser(response.data);
        } catch (error) {
            console.error('Error fetching user details:', error);
            // Only treat this as "logged out" if the token is actually invalid/expired.
            // Other failures (proxy/CORS/temporary backend errors) shouldn't permanently log the user out,
            // otherwise login can look "broken" even when /auth/login succeeds.
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 401) {
                redirectToLogin();
            }
        }
    };

    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken && !publicRoutes.includes(location.pathname)) {
            setIsAuthenticated(true);
            setToken(storedToken);
            fetchUserDetails();
        } else if (!storedToken) {
            setIsAuthenticated(false);
            setToken(null);
            setUser(null);
        }
        setIsLoading(false);
    }, [location.pathname]);

    const login = async (username: string, password: string) => {
        try {
            setIsLoading(true);
            const response = await api.post('/auth/login', { username, password });
            handleSetToken(response.data.token);
            await fetchUserDetails();
            // Route navigation is handled by the caller (LoginPage) to avoid double-navigate races.
        } catch (error: any) {
            if (error.response?.status === 403 && error.response?.data?.renewalUrl) {
                setExpiredAccount({
                    message: error.response.data.message || 'Your license has expired.',
                    renewalUrl: error.response.data.renewalUrl
                });
                return;
            }
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            setIsLoggingOut(true);
            // Call server while the token is still in localStorage so session + open docs are released.
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            clearAuth();
            setIsLoggingOut(false);
            navigate('/login');
        }
    };

    const register = async (username: string, email: string, password: string) => {
        try {
            setIsLoading(true);
            await api.post('/auth/register', { username, email, password });
            navigate('/login');
        } catch (error) {
            console.error('Registration failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const getToken = () => {
        return localStorage.getItem('token');
    };

    const handleSetToken = (newToken: string) => {
        console.log('handleSetToken called with:', newToken);
        setToken(newToken);
        setIsAuthenticated(true);
        localStorage.setItem('token', newToken);
    };

    const updateUser = (updatedUser: UserDetailsDto) => {
        setUser(updatedUser);
    };

    const startImpersonation = async (userId: number, options?: StartImpersonationOptions) => {
        const adminToken = getToken();
        if (!adminToken) {
            throw new Error('Not authenticated');
        }
        const pendingName = options?.documentName?.trim();
        if (pendingName) {
            sessionStorage.setItem(ADMIN_PENDING_DOCUMENT_NAME_KEY, pendingName);
        } else {
            sessionStorage.removeItem(ADMIN_PENDING_DOCUMENT_NAME_KEY);
        }
        const response = await api.post(`/admin/users/${userId}/impersonate`);
        localStorage.setItem(ADMIN_TOKEN_BACKUP_KEY, adminToken);
        handleSetToken(response.data.token);
        await fetchUserDetails();
        navigate('/');
    };

    const endImpersonation = async () => {
        const adminToken = localStorage.getItem(ADMIN_TOKEN_BACKUP_KEY);
        if (!adminToken) {
            clearAuth();
            navigate('/login');
            return;
        }
        handleSetToken(adminToken);
        localStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY);
        await fetchUserDetails();
        navigate('/admin');
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isLoading,
            isLoggingOut,
            user,
            isImpersonating,
            impersonatorUsername,
            login,
            logout,
            register,
            getToken,
            fetchUserDetails,
            setToken: handleSetToken,
            updateUser,
            startImpersonation,
            endImpersonation,
            expiredAccount,
        }}>
            {children}
            {isLoggingOut ? (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: (theme) => theme.zIndex.modal + 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2.5,
                        bgcolor: 'background.default',
                    }}
                >
                    <Box
                        component="img"
                        src="/images/logo.png"
                        alt=""
                        aria-hidden
                        sx={{ height: 48, opacity: 0.9 }}
                    />
                    <CircularProgress size={36} thickness={4} />
                    <Typography variant="body1" color="text.secondary">
                        Signing out…
                    </Typography>
                </Box>
            ) : null}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};